import type { ReactNode } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Portal } from "./portal"
import { useDropdownPosition } from "./dropdown-position"

interface SearchablePickerItem {
  id: string
}

export function SearchablePicker<T extends SearchablePickerItem>({
  items,
  value,
  onChange,
  getLabel,
  getSearchText,
  renderOption,
  label,
  placeholder,
  ariaLabel,
  searchLabel,
  searchPlaceholder,
  emptyMessage,
  footer,
  disabled = false,
}: {
  items: T[]
  value: string
  onChange: (itemId: string) => void
  getLabel: (item: T) => string
  getSearchText: (item: T) => string
  renderOption: (item: T) => ReactNode
  label: ReactNode
  placeholder: string
  ariaLabel?: string
  searchLabel: string
  searchPlaceholder: string
  emptyMessage: string
  footer?: { label: string; onClick: () => void }
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const position = useDropdownPosition(buttonRef, open)
  const selected = items.find((item) => item.id === value)
  const filteredItems = useMemo(() => {
    const search = query.trim().toLowerCase()
    if (!search) return items
    return items.filter((item) => getSearchText(item).toLowerCase().includes(search))
  }, [getSearchText, items, query])

  useEffect(() => {
    if (!open) return
    searchRef.current?.focus()
    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false)
        setQuery("")
      }
    }
    document.addEventListener("pointerdown", handleOutsidePointerDown)
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown)
  }, [open])

  const close = () => {
    setOpen(false)
    setQuery("")
  }

  return (
    <div ref={containerRef} className="relative">
      <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>
      <button
        ref={buttonRef}
        type="button"
        aria-label={selected ? getLabel(selected) : ariaLabel ?? placeholder}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          setOpen((isOpen) => !isOpen)
          setQuery("")
        }}
        className={`flex w-full items-center justify-between rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-left text-sm transition-colors hover:border-ink/30 focus:border-forest-700 focus:outline-none disabled:opacity-60 ${
          selected ? "text-ink" : "text-ink/40"
        }`}
      >
        <span className="truncate">{selected ? getLabel(selected) : placeholder}</span>
        <svg className={`ml-2 h-4 w-4 shrink-0 text-ink/50 transition-transform duration-200 ease-out ${open ? "rotate-180" : ""}`} viewBox="0 0 12 8" fill="none" aria-hidden>
          <path d="m1 1.5 5 5 5-5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && position && (
        <Portal>
          <div
            ref={menuRef}
            role="listbox"
            style={{ left: position.left, width: position.width, maxHeight: position.maxHeight, top: position.top, bottom: position.bottom }}
            className="fixed z-[60] overflow-y-auto rounded-lg border border-gold-500 bg-white shadow-lg"
          >
          <div className="sticky top-0 z-10 bg-white p-1 pb-0">
            <input
              ref={searchRef}
              type="search"
              aria-label={searchLabel}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault()
                  close()
                }
              }}
              placeholder={searchPlaceholder}
              className="w-full rounded-md border border-ink/10 bg-cream-50 px-3 py-2 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div className="p-1 pt-1">
            {filteredItems.length > 0 ? filteredItems.map((item) => (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={item.id === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(item.id)
                  close()
                }}
                className="block w-full rounded px-3 py-2 text-left text-sm text-ink transition-colors duration-150 ease-out hover:bg-cream-100"
              >
                {renderOption(item)}
              </button>
            )) : (
              <p className="px-3 py-2 text-sm text-ink/50">{emptyMessage}</p>
            )}
          </div>
          {footer && (
            <div className="border-t border-gold-500/50 bg-white p-1">
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  close()
                  footer.onClick()
                }}
                className="block w-full rounded px-3 py-2 text-left text-sm font-medium text-forest-700 transition-colors duration-150 ease-out hover:bg-cream-100"
              >
                {footer.label}
              </button>
            </div>
          )}
          </div>
        </Portal>
      )}
    </div>
  )
}
